import {
  establecerLocaleFormato,
  establecerSeparadores,
  formatearExcelConColor,
  obtenerSeparadorDecimal,
} from '@/shared/formula/excelFormat';
import { resolveFormatLocale, type Locale } from '@/shared/i18n/locales';
import { isCurrency, withCurrencyCode, type Currency } from '@/shared/currencies';
import type { CellType } from './types';
import { normalizeSignRule, type GridSignRule } from './signRule';
import { normalizeGridExchangeRate, type GridExchangeRate } from '@/shared/exchangeRates/rule';

/**
 * The named format patterns a grid can apply to a column, ported from TMS
 * `src/lib/formatting.ts`.
 *
 * Patterns are stored CANONICALLY — `.` decimal, `,` thousands, which is
 * Excel's own internal convention — and rendered with the separators the active
 * locale implies. Storing them canonically is what stops switching language
 * from silently changing the meaning of a stored pattern: `$#.##0,00` and
 * `$#,##0.00` are the same pattern in two conventions, and only one can be the
 * stored form.
 *
 * The change from TMS: the separator is no longer a setting anybody edits. It
 * comes from the locale through `Intl`, because CLAUDE.md requires numbers and
 * dates to be formatted with the active locale rather than by hand. An es-AR
 * user reads `1.234,50` and an en user reads `1,234.50` from the very same
 * stored pattern, with nothing to configure and nothing to get out of sync.
 */

import {
  DEFAULT_GLOBAL_FORMATS,
  migrateGlobalFormatKey,
  type GlobalFormatKey,
} from '@/shared/formula/globalFormats';

export type GridFormatKey = GlobalFormatKey;
export type GridFormats = Record<GridFormatKey, string>;

export const DEFAULT_FORMATS: GridFormats = DEFAULT_GLOBAL_FORMATS;
export { DEFAULT_GLOBAL_FORMATS };

/**
 * A tenant's saved patterns as the current keys.
 *
 * `quantity` was one format and is now two: `integer`, the whole-number pattern it
 * always was, and `decimal` beside it. An organization that customized `quantity`
 * keeps that pattern as `integer`; `decimal` has no saved counterpart and so comes
 * from the defaults, which is what spreading over DEFAULT_FORMATS does.
 */
export function migrateStoredFormats(stored: unknown): GridFormats {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return DEFAULT_FORMATS;
  const migrated = Object.fromEntries(
    Object.entries(stored as Record<string, unknown>).map(([key, pattern]) => [
      migrateGlobalFormatKey(key),
      pattern,
    ]),
  ) as Partial<GridFormats>;
  return { ...DEFAULT_FORMATS, ...migrated };
}

/**
 * The decimal separator this locale actually uses, asked of `Intl` rather than
 * hardcoded per language: `1.1` formatted by the locale, and whatever character
 * sits between the digits is the answer.
 */
export function decimalSeparatorFor(locale: Locale): '.' | ',' {
  const parts = new Intl.NumberFormat(locale).formatToParts(1.1);
  const decimal = parts.find((part) => part.type === 'decimal')?.value;
  return decimal === ',' ? ',' : '.';
}

/**
 * Point the Excel pattern renderer at this locale's separators. Called once
 * when the locale is known, and again whenever the user changes it.
 */
export function applyLocaleToFormats(locale: Locale): void {
  establecerSeparadores(decimalSeparatorFor(locale));
  establecerLocaleFormato(resolveFormatLocale(locale));
}

/** Apply an organization's chosen Grid separator without mutating another tenant's state. */
export function setGridDecimalSeparator(separator: '.' | ','): void {
  establecerSeparadores(separator);
}

export function getGridDecimalSeparator(): '.' | ',' {
  return obtenerSeparadorDecimal();
}

/**
 * The font colour a number should take, when its pattern declares one — Excel's
 * `[Red]` on the negative section, which is how a negative balance turns red
 * without any component knowing that rule.
 */
export function numberColor(
  value: number,
  displayedText: string,
  formats: GridFormats = DEFAULT_FORMATS,
): string | undefined {
  const pattern = displayedText.includes('%')
    ? formats.percent
    : /[$€]|\b[A-Z]{3}\b/.test(displayedText)
      ? formats.currency
      : formats.integer;
  return formatearExcelConColor(value, pattern).color;
}

// ── Per-column data formats ───────────────────────────────────────────────────
//
// Ported from TMS `src/lib/gridDataFormats.ts`. Deliberately separate from
// formulas: a formula decides a value, this decides how that value is shown.
//
// The format keys are English here and map onto `GridFormats` above, so a
// column stores `{ type: 'number', formatKey: 'currency' }` rather than a
// Spanish setting name.

import {
  formatearExcel,
  numeroEditable,
  parsearNumeroExcel,
} from '@/shared/formula/excelFormat';
import type { RollupConfig, RollupAggregate, RollupColumnType } from '@/shared/formula/rollups';
export type { RollupConfig, RollupAggregate, RollupColumnType };

export type GridDataType = 'auto' | 'text' | 'date' | 'number' | 'smartchip' | 'autofill' | 'rollup' | 'lookup' | 'attachment' | 'barcode' | 'checkbox';
export type ColumnAlign = 'left' | 'center' | 'right' | 'auto';
export type ColumnWrapMode = 'wrap' | 'clip' | 'shrink' | 'expand' | 'overflow';

export type GridInvalidMode = 'warn' | 'reject';

/** A full-height line down one side of a column. */
export type GridBorderWeight = 'thin' | 'medium' | 'thick';
export interface GridColumnBorder {
  weight: GridBorderWeight;
  color: string;
}
export const GRID_BORDER_WEIGHTS: readonly GridBorderWeight[] = ['thin', 'medium', 'thick'];
export const GRID_BORDER_WIDTHS: Readonly<Record<GridBorderWeight, number>> = { thin: 1, medium: 2, thick: 3 };

function normalizeGridColumnBorder(value: unknown): GridColumnBorder | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const { weight, color } = value as Record<string, unknown>;
  if (!(GRID_BORDER_WEIGHTS as readonly unknown[]).includes(weight) || typeof color !== 'string' || color === '') return undefined;
  return { weight: weight as GridBorderWeight, color };
}

const LAST_BORDER_KEY = 'numtable-grid-last-border';

/** The weight and color this browser last chose for a border; a new border starts with it. */
export function lastBorderStyle(): GridColumnBorder {
  try {
    const stored = normalizeGridColumnBorder(JSON.parse(localStorage.getItem(LAST_BORDER_KEY) ?? 'null'));
    if (stored) return stored;
  } catch { /* unavailable or unreadable storage falls back to the default */ }
  return { weight: 'thin', color: '#000000' };
}

export function rememberBorderStyle(border: GridColumnBorder): void {
  try { localStorage.setItem(LAST_BORDER_KEY, JSON.stringify(border)); } catch { /* best effort */ }
}

/** The inset shadows that draw a column's own borders on each of its cells and its heading. */
export function columnBorderShadows(format: Pick<GridDataFormat, 'borderLeft' | 'borderRight'> | undefined): string[] {
  if (!format) return [];
  return [
    format.borderLeft ? `inset ${GRID_BORDER_WIDTHS[format.borderLeft.weight]}px 0 0 0 ${format.borderLeft.color}` : '',
    format.borderRight ? `inset -${GRID_BORDER_WIDTHS[format.borderRight.weight]}px 0 0 0 ${format.borderRight.color}` : '',
  ].filter(Boolean);
}

/** The types one cell of a by-cell (`auto`) column can take on its own. */
export type GridCellDataType = 'auto' | 'text' | 'date' | 'number' | 'checkbox';
export const GRID_CELL_DATA_TYPES: readonly GridCellDataType[] = ['auto', 'text', 'date', 'number', 'checkbox'];

/** One cell's own type and number/date format, kept in its column's `cells`. */
export interface GridCellFormat {
  type: GridCellDataType;
  formatKey?: GridFormatKey;
  currency?: Currency;
  cents?: boolean;
  customPattern?: string;
}

export interface GridDataFormat {
  type: GridDataType;
  formatKey?: GridFormatKey;
  /** Only read with the `currency` format; replaces the pattern's `$`. */
  currency?: Currency;
  /** Only read with the `currency` format; unset follows the pattern's own decimals. */
  cents?: boolean;
  /** A canonical Excel pattern typed for this column; overrides `formatKey`. */
  customPattern?: string;
  align?: ColumnAlign;
  wrapMode?: ColumnWrapMode;
  bold?: boolean;
  borderLeft?: GridColumnBorder;
  borderRight?: GridColumnBorder;
  /** Group subtotal cells: their own wrapping; unset follows `wrapMode`. */
  subtotalWrapMode?: ColumnWrapMode;
  subtotalBold?: boolean;
  /** Column heading overrides; unset follows the organization's header default. */
  headerMode?: 'default' | 'same' | 'custom';
  headerAlign?: ColumnAlign;
  headerWrapMode?: ColumnWrapMode;
  /** Heading preferences saved without introducing a cell-format override. */
  headerOnly?: boolean;
  /** Curated option list for `smartchip` / `autofill` columns. */
  options?: readonly GridDataOption[];
  /**
   * When set, the option list is borrowed from another column instead of being
   * written by hand. `options` then only carries the colours: the values
   * themselves come from the linked column and follow its edits.
   */
  optionsLink?: GridOptionsLink;
  /** Configuration for relational rollups and lookups across linked sheets */
  rollup?: RollupConfig;
  /** What a typed value its column rejects does: kept and flagged (`warn`, the
   * default) or refused (`reject`): a typed value keeps the cell in edit mode, a pasted one
   * is left out and the previous value stays. */
  invalid?: GridInvalidMode;
  /** Number columns: the sign follows another column of the same row (see `signRule.ts`). */
  sign?: GridSignRule;
  /** Number columns: an exchange-rate column that fills itself with the rate
   *  of `from` → `to` on the row's `dateField` (see `exchangeRateRule.ts`). */
  exchangeRate?: GridExchangeRate;
  /** By-cell (`auto`) columns only: row key → that cell's own type and format.
   *  Any other column type is one type for the whole column, so it has none. */
  cells?: Readonly<Record<string, GridCellFormat>>;
  /** Configuration for attachments */
  attachmentConfig?: {
    maxFileSizeMb?: number;
    allowedMimeTypes?: readonly string[];
  };
}

export { normalizeGridExchangeRate, type GridExchangeRate, type GridExchangeRateSideRule } from '@/shared/exchangeRates/rule';

/**
 * The exchange-rate setup a column runs with. A module preset marked `fixed`
 * (Investments' rate column) keeps its pair and date column whatever was saved;
 * market, side and overwriting stay the reader's choice. Otherwise a saved
 * format decides, and a saved format without one turns the type off.
 */
export function resolveGridExchangeRate(
  stored: GridDataFormat | undefined,
  preset: GridDataFormat | undefined,
): GridExchangeRate | undefined {
  const fixed = preset?.exchangeRate?.fixed ? preset.exchangeRate : undefined;
  if (fixed) {
    const saved = stored?.exchangeRate;
    return {
      ...fixed,
      ...(saved?.market ? { market: saved.market } : {}),
      ...(saved?.side ? { side: saved.side } : {}),
      ...(saved?.sideRule ? { sideRule: saved.sideRule } : {}),
      ...(saved?.allowOverwrite === false ? { allowOverwrite: false } : {}),
    };
  }
  return stored ? stored.exchangeRate : preset?.exchangeRate;
}

/** The column a smartchip/autofill list borrows its values from. */
export interface GridOptionsLink {
  /** Omitted for a tab of the document being edited. */
  documentId?: string | null;
  tabId: string;
  field: string;
  /** `alpha` sorts A→Z; `source` keeps the order the source column has. */
  sort: 'alpha' | 'source';
}

/** One entry of a smartchip/autofill list: the value plus its chip colour. */
export interface GridDataOption {
  value: string;
  color?: string | null;
}

/** Keep the stored list small and free of blanks/duplicates. */
export const MAX_GRID_DATA_OPTIONS = 200;

/** Accepts both the plain-string form and the `{ value, color }` form. */
export function normalizeGridDataOptions(value: unknown): GridDataOption[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const byValue = new Map<string, GridDataOption>();
  for (const raw of value) {
    const text = typeof raw === 'string'
      ? raw
      : typeof (raw as { value?: unknown })?.value === 'string'
        ? (raw as { value: string }).value
        : null;
    if (text === null) continue;
    const option = text.trim();
    if (option === '' || byValue.has(option)) continue;
    const color = typeof raw === 'object' && raw !== null ? (raw as { color?: unknown }).color : undefined;
    byValue.set(option, typeof color === 'string' && color.trim() !== ''
      ? { value: option, color }
      : { value: option });
    if (byValue.size >= MAX_GRID_DATA_OPTIONS) break;
  }
  return byValue.size > 0 ? Array.from(byValue.values()) : undefined;
}

export function normalizeGridOptionsLink(value: unknown): GridOptionsLink | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const { documentId, tabId, field, sort } = value as Record<string, unknown>;
  if (typeof tabId !== 'string' || tabId === '' || typeof field !== 'string' || field === '') return undefined;
  return {
    ...(typeof documentId === 'string' && documentId !== '' ? { documentId } : {}),
    tabId,
    field,
    sort: sort === 'source' ? 'source' : 'alpha',
  };
}

export function normalizeRollupConfig(value: unknown): RollupConfig | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const { type, sourceCol, targetDoc, targetTab, targetMatchCol, targetCol, agg, separator } = value as Record<string, unknown>;
  if (type !== 'rollup' && type !== 'lookup') return undefined;
  if (typeof sourceCol !== 'string' || sourceCol === '' || typeof targetTab !== 'string' || targetTab === '' || typeof targetCol !== 'string' || targetCol === '') {
    return undefined;
  }
  const validAggs: Set<string> = new Set(['SUM', 'AVG', 'COUNT', 'MIN', 'MAX']);
  return {
    type,
    sourceCol,
    ...(typeof targetDoc === 'string' && targetDoc !== '' ? { targetDoc } : {}),
    targetTab,
    ...(typeof targetMatchCol === 'string' && targetMatchCol !== '' ? { targetMatchCol } : {}),
    targetCol,
    ...(typeof agg === 'string' && validAggs.has(agg.toUpperCase()) ? { agg: agg.toUpperCase() as RollupAggregate } : {}),
    ...(typeof separator === 'string' ? { separator } : {}),
  };
}

/** `gridKey` → column key → format. */
export type GridDataFormats = Record<string, Record<string, GridDataFormat>>;

export const GRID_DATE_FORMATS: readonly GridFormatKey[] = [
  'date',
  'dateWeekday',
  'dateTime',
  'dateTimeWeekday',
  'dayMonth',
  'monthYearLong',
  'monthYearShort',
];

export const GRID_NUMBER_FORMATS: readonly GridFormatKey[] = [
  'currency',
  'integer',
  'decimal',
  'percent',
];

const FORMAT_KEYS = new Set<string>([...GRID_DATE_FORMATS, ...GRID_NUMBER_FORMATS]);
const ALIGN_KEYS = new Set<string>(['left', 'center', 'right', 'auto']);
const WRAP_MODE_KEYS = new Set<string>(['wrap', 'clip', 'shrink', 'expand', 'overflow']);

/**
 * Discard anything malformed. This value comes from the database, so a corrupt
 * or future-version blob must degrade one column's appearance rather than crash
 * every grid.
 */
export function normalizeGridDataFormats(value: unknown): GridDataFormats {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result: GridDataFormats = {};

  for (const [gridKey, rawGrid] of Object.entries(value as Record<string, unknown>)) {
    if (!rawGrid || typeof rawGrid !== 'object' || Array.isArray(rawGrid)) continue;
    const columns: Record<string, GridDataFormat> = {};

    for (const [field, raw] of Object.entries(rawGrid as Record<string, unknown>)) {
      const type = typeof raw === 'string' ? raw : (raw as { type?: unknown })?.type;
      const formatKey =
        typeof raw === 'object' && raw !== null
          ? (raw as { formatKey?: unknown }).formatKey
          : undefined;
      const currency =
        typeof raw === 'object' && raw !== null
          ? (raw as { currency?: unknown }).currency
          : undefined;
      const align =
        typeof raw === 'object' && raw !== null
          ? (raw as { align?: unknown }).align
          : undefined;
      const wrapMode =
        typeof raw === 'object' && raw !== null
          ? (raw as { wrapMode?: unknown }).wrapMode
          : undefined;
      const bold =
        typeof raw === 'object' && raw !== null
          ? (raw as { bold?: unknown }).bold
          : undefined;
      const options =
        typeof raw === 'object' && raw !== null
          ? normalizeGridDataOptions((raw as { options?: unknown }).options)
          : undefined;
      const optionsLink =
        typeof raw === 'object' && raw !== null
          ? normalizeGridOptionsLink((raw as { optionsLink?: unknown }).optionsLink)
          : undefined;
      const rollup =
        typeof raw === 'object' && raw !== null
          ? normalizeRollupConfig((raw as { rollup?: unknown }).rollup)
          : undefined;

      if (field !== '' && (type === 'auto' || type === 'text' || type === 'date' || type === 'number' || type === 'smartchip' || type === 'autofill' || type === 'rollup' || type === 'lookup' || type === 'attachment' || type === 'barcode' || type === 'checkbox')) {
        const attachmentConfig = typeof raw === 'object' && raw !== null && 'attachmentConfig' in raw
          ? (raw as { attachmentConfig?: { maxFileSizeMb?: number; allowedMimeTypes?: readonly string[] } }).attachmentConfig
          : undefined;
        columns[field] = {
          type,
          ...(() => {
            if (!raw || typeof raw !== 'object') return {};
            const heading = raw as Partial<GridDataFormat>;
            return {
              ...(heading.headerMode === 'default' || heading.headerMode === 'same' || heading.headerMode === 'custom' ? { headerMode: heading.headerMode } : {}),
              ...(heading.headerOnly === true ? { headerOnly: true } : {}),
              ...(typeof heading.headerAlign === 'string' && ALIGN_KEYS.has(heading.headerAlign) ? { headerAlign: heading.headerAlign } : {}),
              ...(typeof heading.headerWrapMode === 'string' && WRAP_MODE_KEYS.has(heading.headerWrapMode) ? { headerWrapMode: heading.headerWrapMode } : {}),
            };
          })(),
          ...(typeof formatKey === 'string' && FORMAT_KEYS.has(migrateGlobalFormatKey(formatKey))
            ? { formatKey: migrateGlobalFormatKey(formatKey) as GridFormatKey }
            : {}),
          ...(isCurrency(currency) ? { currency } : {}),
          ...(typeof raw === 'object' && raw !== null && typeof (raw as { customPattern?: unknown }).customPattern === 'string'
            && (type === 'number' || type === 'date' || type === 'rollup')
            ? { customPattern: (raw as { customPattern: string }).customPattern }
            : {}),
          ...(typeof raw === 'object' && raw !== null && typeof (raw as { cents?: unknown }).cents === 'boolean'
            ? { cents: (raw as { cents: boolean }).cents }
            : {}),
          ...(typeof align === 'string' && ALIGN_KEYS.has(align)
            ? { align: align as ColumnAlign }
            : {}),
          ...(typeof wrapMode === 'string' && WRAP_MODE_KEYS.has(wrapMode)
            ? { wrapMode: wrapMode as ColumnWrapMode }
            : {}),
          ...(typeof bold === 'boolean' ? { bold } : {}),
          ...(() => {
            if (!raw || typeof raw !== 'object') return {};
            const borderLeft = normalizeGridColumnBorder((raw as { borderLeft?: unknown }).borderLeft);
            const borderRight = normalizeGridColumnBorder((raw as { borderRight?: unknown }).borderRight);
            return { ...(borderLeft ? { borderLeft } : {}), ...(borderRight ? { borderRight } : {}) };
          })(),
          ...(typeof raw === 'object' && raw !== null && WRAP_MODE_KEYS.has(String((raw as { subtotalWrapMode?: unknown }).subtotalWrapMode))
            ? { subtotalWrapMode: (raw as { subtotalWrapMode: ColumnWrapMode }).subtotalWrapMode }
            : {}),
          ...(typeof raw === 'object' && raw !== null && typeof (raw as { subtotalBold?: unknown }).subtotalBold === 'boolean'
            ? { subtotalBold: (raw as { subtotalBold: boolean }).subtotalBold }
            : {}),
          ...(options && (type === 'smartchip' || type === 'autofill') ? { options } : {}),
          ...(optionsLink && (type === 'smartchip' || type === 'autofill') ? { optionsLink } : {}),
          ...(rollup && (type === 'rollup' || type === 'lookup') ? { rollup } : {}),
          ...(attachmentConfig ? { attachmentConfig } : {}),
          ...(() => {
            const cells = type === 'auto' && typeof raw === 'object' && raw !== null
              ? normalizeGridCellFormats((raw as { cells?: unknown }).cells)
              : undefined
            return cells ? { cells } : {}
          })(),
          ...(() => {
            const sign = type === 'number' && typeof raw === 'object' && raw !== null
              ? normalizeSignRule((raw as { sign?: unknown }).sign)
              : null
            return sign ? { sign } : {}
          })(),
          ...(() => {
            const exchangeRate = type === 'number' && typeof raw === 'object' && raw !== null
              ? normalizeGridExchangeRate((raw as { exchangeRate?: unknown }).exchangeRate)
              : undefined
            return exchangeRate ? { exchangeRate } : {}
          })(),
          ...(typeof raw === 'object' && raw !== null && (raw as { invalid?: unknown }).invalid === 'reject'
            && (type === 'number' || type === 'date' || type === 'smartchip' || type === 'autofill')
            ? { invalid: 'reject' as const }
            : {}),
        };
      }
    }

    if (Object.keys(columns).length > 0) result[gridKey] = columns;
  }
  return result;
}

function normalizeGridCellFormats(value: unknown): Record<string, GridCellFormat> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const cells: Record<string, GridCellFormat> = {};
  for (const [rowKey, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object') continue;
    const { type, formatKey, currency, cents, customPattern } = raw as Record<string, unknown>;
    if (!(GRID_CELL_DATA_TYPES as readonly unknown[]).includes(type)) continue;
    const numeric = type === 'number' || type === 'date';
    cells[rowKey] = {
      type: type as GridCellDataType,
      ...(numeric && typeof formatKey === 'string' && FORMAT_KEYS.has(migrateGlobalFormatKey(formatKey))
        ? { formatKey: migrateGlobalFormatKey(formatKey) as GridFormatKey }
        : {}),
      ...(type === 'number' && isCurrency(currency) ? { currency } : {}),
      ...(type === 'number' && typeof cents === 'boolean' ? { cents } : {}),
      ...(numeric && typeof customPattern === 'string' ? { customPattern } : {}),
    };
  }
  return Object.keys(cells).length > 0 ? cells : undefined;
}

/**
 * The format one cell renders and reads with. A by-cell column hands each cell
 * its own (`auto` when it has none); any other column is the same everywhere.
 */
export function gridCellDataFormat(format: GridDataFormat, rowKey: string): GridDataFormat {
  if (format.type !== 'auto') return format;
  const cell = format.cells?.[rowKey];
  return cell ? { ...cell } : { type: 'auto' };
}

/** A by-cell column with these cells' formats replaced; null clears a cell back to `auto`. */
export function withGridCellFormats(
  format: GridDataFormat,
  changes: ReadonlyMap<string, GridCellFormat | null>,
): GridDataFormat {
  const cells: Record<string, GridCellFormat> = { ...(format.cells ?? {}) };
  for (const [rowKey, cell] of changes) {
    if (!cell || (cell.type === 'auto' && Object.keys(cell).length === 1)) delete cells[rowKey];
    else cells[rowKey] = cell;
  }
  const { cells: _previous, ...rest } = format;
  return Object.keys(cells).length > 0 ? { ...rest, type: 'auto', cells } : { ...rest, type: 'auto' };
}

/** The value a typed or pasted text stores in a cell of this (cell-resolved) format. */
export function parseGridCellInput(
  text: string,
  format: GridDataFormat,
  formats: GridFormats = DEFAULT_FORMATS,
): unknown {
  if (format.type === 'auto') return parseGridAutoCell(text, formats);
  if (format.type === 'number') return parseGridNumberCell(text, formats, format);
  if (format.type === 'date') {
    const date = parseGridDate(text);
    if (!date) return text.trim() === '' ? null : text;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }
  if (format.type === 'checkbox') return normalizeGridCellValue(text, 'checkbox') === true;
  return text;
}

export function withGridDataFormat(
  all: GridDataFormats,
  gridKey: string,
  field: string,
  format: GridDataFormat,
): GridDataFormats {
  return { ...all, [gridKey]: { ...(all[gridKey] ?? {}), [field]: format } };
}

import { parseGridDate, serialToDate } from '@/shared/formula/dateSerial';
export { parseGridDate };

export function clipboardGridData(
  value: unknown,
  format: GridDataFormat,
  formats: GridFormats,
): string {
  if (value === null || value === undefined || value === '') return '';
  if (format.type === 'date') {
    const datePattern = formats.date || DEFAULT_FORMATS.date;
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime())
        ? formatearExcel(value, datePattern)
        : String(value);
    }
    if (typeof value === 'number' && value > 1000 && value < 100000) {
      const localDate = serialToDate(value);
      return formatearExcel(localDate, datePattern);
    }
    const raw = String(value);
    const parsed = parseGridDate(raw);
    return parsed ? formatearExcel(parsed, datePattern) : raw;
  }
  return formatGridData(value, format, formats);
}

export const patternHasCents = (pattern: string): boolean => /\.[0#]/.test(pattern);

/** A canonical pattern with its decimals forced on (two) or off; `undefined` leaves it as is. */
export function withCents(pattern: string, cents: boolean | undefined): string {
  if (cents === undefined || cents === patternHasCents(pattern)) return pattern;
  return cents
    ? pattern.replace(/([#0,]*0)(?![0#,.])/g, '$1.00')
    : pattern.replace(/\.[0#]+/g, '');
}

/** The pattern a column renders with: its custom one, else its named format's. */
export function gridPatternFor(format: GridDataFormat, formats: GridFormats, fallback: GridFormatKey): string {
  return format.customPattern !== undefined ? format.customPattern : formats[format.formatKey ?? fallback];
}

export function formatGridData(
  value: unknown,
  format: GridDataFormat,
  formats: GridFormats,
): string {
  if (value === null || value === undefined || value === '') return '';
  if (format.type === 'text') return String(value);

  if (format.type === 'auto') {
    // Like Excel's General: numbers in full with no grouping, typed dates in the date format.
    if (typeof value === 'number') return Number.isFinite(value) ? numeroEditable(value) : String(value);
    if (typeof value === 'string' && STORED_DATE.test(value.trim())) {
      const parsed = parseGridDate(value);
      if (parsed) return formatearExcel(parsed, formats.date);
    }
    return String(value);
  }

  if (format.type === 'number') {
    const n =
      typeof value === 'number'
        ? value
        : Number(String(value).replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(n)) return String(value);
    if (format.customPattern !== undefined || format.formatKey !== 'currency') return formatearExcel(n, gridPatternFor(format, formats, 'integer'));
    const text = formatearExcel(n, withCents(formats.currency, format.cents));
    return withCurrencyCode(text, format.currency);
  }

  if (format.type === 'date') {
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime())
        ? formatearExcel(value, gridPatternFor(format, formats, 'date'))
        : String(value);
    }
    if (typeof value === 'number' && value > 1000 && value < 100000) {
      // Excel serial date number
      const localDate = serialToDate(value);
      return formatearExcel(localDate, gridPatternFor(format, formats, 'date'));
    }
    const raw = String(value);
    const parsed = parseGridDate(raw);
    return parsed ? formatearExcel(parsed, gridPatternFor(format, formats, 'date')) : raw;
  }

  if (format.type === 'rollup') {
    const n =
      typeof value === 'number'
        ? value
        : Number(String(value).replace(/\./g, '').replace(',', '.'));
    if (Number.isFinite(n)) {
      return formatearExcel(n, gridPatternFor(format, formats, 'integer'));
    }
    return String(value);
  }

  if (format.type === 'lookup') {
    return String(value);
  }

  return String(value);
}

/**
 * The inverse for numeric columns: what the user sees and types, back to a
 * number. Returns null when the text is not one, an empty cell included.
 *
 * With no configured format it still honours the separators — typing `0,1` has
 * to mean `0.1` everywhere, not only where somebody picked a format.
 */
export function parseGridNumber(
  text: string,
  formats: GridFormats,
  format?: GridDataFormat,
): number | null {
  const pattern =
    format?.type === 'number' ? gridPatternFor(format, formats, 'integer') : '';
  return parsearNumeroExcel(text, pattern);
}

/**
 * The text an edit STARTS from: the real value, not the formatted one.
 *
 * A format decides how many decimals are SHOWN, never how many are kept - `#,##0`
 * displays 3.993,62 as 3.994 - and the editor commits what it holds. Starting an edit
 * from the displayed text would therefore make merely opening a cell and confirming it
 * round the stored number away. So the cell shows the format and the editor shows the
 * value, which is Excel's split between the cell and the formula bar.
 */
export function editableGridNumber(
  value: unknown,
  formats: GridFormats,
  format?: GridDataFormat,
): string {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'number') return format ? formatGridData(value, format, formats) : String(value);
  if (format?.type === 'date') return formatGridData(value, format, formats);

  // A percent pattern displays a value already scaled x100 and parses it back the same
  // way, so its real value is edited in those units too: 0,123456 as 12,3456%. Dropping
  // the sign here would make the commit divide by 100 a second time.
  const pattern =
    format?.type === 'number' || format?.type === 'rollup'
      ? gridPatternFor(format, formats, 'integer')
      : '';
  return pattern.includes('%') ? `${numeroEditable(scaleToPercent(value))}%` : numeroEditable(value);
}

/** x100 without the trailing binary noise that makes 0.1234 read as 12.340000000000002. */
function scaleToPercent(value: number): number {
  const scaled = value * 100;
  return Number.isFinite(scaled) ? Number(scaled.toPrecision(15)) : scaled;
}

/**
 * Normalizes an individual cell value according to its column type or data format.
 * Converts dirty strings (like "1.250,50 USD", "-", "—", whitespace) into clean numbers,
 * dates, booleans or strings.
 */
/**
 * True when text reads as a number, allowing the decorations numbers carry
 * when copied (currency symbols or codes, separators, sign, percent). Text that
 * merely contains digits - `q1`, `12 kg` - is not a number, and is kept as
 * typed rather than having its letters stripped.
 */
export function looksLikeGridNumber(text: string): boolean {
  const cleaned = text
    .replace(/[\s ]/g, '')
    .replace(/^[A-Z]{3}|[A-Z]{3}$/g, '')
    .replace(/[$€£¥]/g, '');
  return /^[+-]?(\d[\d.,]*|[.,]\d+)%?$/.test(cleaned) || /^[+-]?\d+(\.\d+)?e[+-]?\d+$/i.test(cleaned);
}

/** Search text that filters a grid to the rows holding type errors. */
export const GRID_ERRORS_SEARCH = '#errors';

export function isGridErrorsSearch(search: string): boolean {
  return search.trim().toLowerCase() === GRID_ERRORS_SEARCH;
}

type TypedColumn = { id?: string; field?: string; cellType?: CellType | null; dataFormat?: GridDataFormat };

function isNumberColumn(column: TypedColumn): boolean {
  return column.cellType === 'number' || column.dataFormat?.type === 'number';
}

function isDateColumn(column: TypedColumn): boolean {
  return column.cellType === 'date' || column.dataFormat?.type === 'date';
}

/** Dates are stored as ISO text; anything else in a date column was kept as typed. */
const STORED_DATE = /^\d{4}-\d{2}-\d{2}(T.*)?$/;

/** A number or date column holding text it could not read: kept so nothing is lost, but flagged. */
export function isGridTypeMismatch(value: unknown, column: TypedColumn): boolean {
  if (typeof value !== 'string' || value.trim() === '') return false;
  if (isNumberColumn(column)) return true;
  return isDateColumn(column) && !STORED_DATE.test(value.trim());
}

/** A list column (autofill, smart chip) holding a value that is not one of its options.
 * An empty option list is still loading or not set up, so nothing is flagged against it. */
export function isGridListMismatch(value: unknown, options: readonly { value: string }[]): boolean {
  if (options.length === 0 || value === null || value === undefined) return false;
  const text = String(value).trim();
  return text !== '' && !options.some((option) => option.value === text);
}

/** How many cells of these rows hold a value their column's type rejects. */
export function countGridTypeErrors(
  rows: readonly Record<string, unknown>[],
  columns: readonly TypedColumn[],
): number {
  const numeric = columns.filter((column) => isNumberColumn(column) || isDateColumn(column));
  let count = 0;
  for (const row of rows) {
    for (const column of numeric) {
      const key = column.field ?? column.id;
      if (key && isGridTypeMismatch(row[key], column)) count += 1;
    }
  }
  return count;
}

/**
 * The value a number cell stores for typed or pasted text: a number when the
 * text is one, null when blank, and otherwise the text itself.
 */
export function parseGridNumberCell(
  text: string,
  formats: GridFormats = DEFAULT_FORMATS,
  format?: GridDataFormat,
): number | string | null {
  const trimmed = text.trim();
  if (trimmed === '' || trimmed === '-' || trimmed === '—') return null;
  if (!looksLikeGridNumber(trimmed)) return trimmed;
  const parsed = normalizeGridCellValue(trimmed, 'number', formats, format);
  return typeof parsed === 'number' ? parsed : trimmed;
}

/** A whole cell that is a date: `d/m`, `d/m/yy(yy)` or ISO, never a date inside other text. */
const AUTO_DATE = /^(\d{1,2}[/-]\d{1,2}([/-]\d{2,4})?|\d{4}-\d{2}-\d{2})$/;

/**
 * The value an `auto` column stores for typed or pasted text, the way Excel's
 * General format reads it: a number when it is one, an ISO date when it is a
 * date, a boolean for TRUE/FALSE, and otherwise the text as typed.
 */
export function parseGridAutoCell(
  text: string,
  formats: GridFormats = DEFAULT_FORMATS,
): number | string | boolean | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (looksLikeGridNumber(trimmed)) {
    const parsed = parseGridNumberCell(trimmed, formats);
    if (typeof parsed === 'number') return parsed;
  }
  if (AUTO_DATE.test(trimmed)) {
    const date = parseGridDate(trimmed);
    if (date) {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    }
  }
  const upper = trimmed.toUpperCase();
  if (upper === 'TRUE' || upper === 'FALSE') return upper === 'TRUE';
  return text;
}

/**
 * Pasted numbers carry whichever separators their source used. With only one kind
 * present: repeated, it groups thousands (37.563.230); once, it is the decimal mark
 * unless exactly three digits follow (1.234), which is ambiguous and so read with the
 * active decimal separator.
 */
function parseSingleSeparatorNumber(cleaned: string, separator: '.' | ','): number {
  const parts = cleaned.split(separator);
  const isThousands =
    parts.length > 2
      ? parts.slice(1).every((part) => /^\d{3}$/.test(part))
      : /^\d{3}$/.test(parts[1] ?? '') && /\d$/.test(parts[0] ?? '') && getGridDecimalSeparator() !== separator;
  if (parts.length > 2 && !isThousands) return Number.NaN;
  return Number(isThousands ? parts.join('') : parts.join('.'));
}

export function normalizeGridCellValue(
  value: unknown,
  cellType?: CellType | null,
  formats: GridFormats = DEFAULT_FORMATS,
  dataFormat?: GridDataFormat,
  options: { keepText?: boolean } = {},
): unknown {
  if (value === undefined || value === null) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '-' || trimmed === '—') return null;
  }

  if (cellType === 'number' || dataFormat?.type === 'number') {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (options.keepText && !looksLikeGridNumber(trimmed)) return trimmed;
      // A percent column reads what is typed in percent units: `1` is 1%, not 100%. The
      // shortcuts below would keep the bare number, so these go to the scaling parser.
      if (dataFormat?.type === 'number' && gridPatternFor(dataFormat, formats, 'integer').includes('%')) {
        return parseGridNumber(trimmed, formats, dataFormat);
      }
      const cleaned = trimmed.replace(/[$€£¥\s]/g, '');
      if (/^-?\d+$/.test(cleaned)) {
        const direct = Number(cleaned);
        return Number.isFinite(direct) ? direct : null;
      }
      const hasDot = cleaned.includes('.');
      const hasComma = cleaned.includes(',');
      if (hasDot && hasComma) {
        const lastDot = cleaned.lastIndexOf('.');
        const lastComma = cleaned.lastIndexOf(',');
        if (lastDot < lastComma) {
          const num = Number(cleaned.replaceAll('.', '').replace(',', '.'));
          return Number.isFinite(num) ? num : null;
        } else {
          const num = Number(cleaned.replaceAll(',', ''));
          return Number.isFinite(num) ? num : null;
        }
      }
      if (hasComma || hasDot) {
        const num = parseSingleSeparatorNumber(cleaned, hasComma ? ',' : '.');
        return Number.isFinite(num) ? num : null;
      }
      const parsed = parseGridNumber(value, formats, dataFormat);
      if (parsed !== null) return parsed;
      const strippedStr = cleaned.replace(/[^0-9.,+-]/g, '').replace(',', '.');
      if (strippedStr === '' || strippedStr === '-' || strippedStr === '+') return null;
      const stripped = Number(strippedStr);
      return Number.isFinite(stripped) ? stripped : null;
    }
    return null;
  }

  if (cellType === 'date' || dataFormat?.type === 'date') {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
    }
    if (typeof value === 'number' && value > 1000 && value < 100000) {
      const date = serialToDate(value);
      return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
    }
    if (typeof value === 'string') {
      const parsed = parseGridDate(value);
      return parsed ? parsed.toISOString().slice(0, 10) : value.trim();
    }
    return null;
  }

  if (cellType === 'checkbox') {
    if (typeof value === 'boolean') return value;
    if (value === 'true' || value === 1 || value === '1') return true;
    if (value === 'false' || value === 0 || value === '0') return false;
    return Boolean(value);
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  return value;
}

/**
 * Normalizes an entire grid row based on the column definitions.
 */
export function normalizeGridRow<T extends Record<string, unknown>>(
  row: T,
  columns: readonly { id?: string; field?: string; cellType?: CellType | null; dataFormat?: GridDataFormat }[],
  formats: GridFormats = DEFAULT_FORMATS,
): T {
  const normalized: Record<string, unknown> = { ...row };
  for (const col of columns) {
    const key = col.id ?? col.field;
    if (key && key in normalized) {
      normalized[key] = normalizeGridCellValue(normalized[key], col.cellType, formats, col.dataFormat, { keepText: true });
    }
  }
  return normalized as T;
}

